import React from 'react';
import { Banknote, Users, HandHeart, TrendingUp, Award, Target, CreditCard, Building2, Copy, Check, Heart, Sparkles } from 'lucide-react';

const BankDetails = () => {
    const [copiedField, setCopiedField] = React.useState(null);

    const bankData = [
        { label: "Banco", value: "Millennium BIM", icon: <Building2 size={14} /> },
        { label: "Designação", value: "UEM ALUMNI", icon: <Users size={14} /> },
        { label: "Conta Nr.", value: "381798888", icon: <CreditCard size={14} /> },
        { label: "IBAN", value: "MZ590001000000179888857", icon: <Banknote size={14} /> },
        { label: "NIB", value: "0001000000038179888857", icon: <Banknote size={14} /> }
    ];

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        setCopiedField(label);
        setTimeout(() => setCopiedField(null), 2000);
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-50">
                <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-medium text-gray-700">Dados Bancários</h3>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                    Contribua para o desenvolvimento da Comunidade Alumni-UEM
                </p>
            </div>
            <div className="divide-y divide-gray-50">
                {bankData.map((item, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-gray-50/50 transition-colors">
                        <div className="flex items-center gap-3 mb-2 sm:mb-0">
                            <span className="text-gray-400">
                                {item.icon}
                            </span>
                            <span className="text-xs font-medium text-gray-500 min-w-[80px]">
                                {item.label}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 font-mono bg-gray-50 px-3 py-1 rounded-lg">
                                {item.value}
                            </span>
                            <button
                                onClick={() => handleCopy(item.value, item.label)}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Copiar"
                            >
                                {copiedField === item.label ? (
                                    <Check size={12} className="text-emerald-500" />
                                ) : (
                                    <Copy size={12} className="text-gray-300 hover:text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ContributionWays = () => {
    const ways = [
        {
            icon: <Banknote className="w-5 h-5" />,
            title: "Transferência Bancária",
            description: "Realize transferências diretamente para a conta do Fundo Alumni"
        },
        {
            icon: <HandHeart className="w-5 h-5" />,
            title: "Doação Mensal",
            description: "Contribua com um valor mensal para apoiar projetos contínuos"
        },
        {
            icon: <Users className="w-5 h-5" />,
            title: "Patrocínio de Eventos",
            description: "Apoie eventos e iniciativas da comunidade Alumni"
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            {ways.map((way, index) => (
                <div key={index} className="text-center p-4 rounded-xl hover:bg-white transition-colors">
                    <div className="flex justify-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                            <div className="text-emerald-500">
                                {way.icon}
                            </div>
                        </div>
                    </div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">{way.title}</h4>
                    <p className="text-xs text-gray-400">{way.description}</p>
                </div>
            ))}
        </div>
    );
};

const FundoAlumni = () => {
    const achievements = [
        { value: "+500", label: "Contribuintes Activos" },
        { value: "10+", label: "Projetos Apoiados" },
        { value: "1000+", label: "Estudantes Beneficiados" }
    ];

    return (
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
            {/* Header Section */}
             <div className="mb-12 bg-emerald-50 rounded-xl p-6 border border-emerald-100/50">
                <div className="border-l-4 border-emerald-500 pl-4 mb-4">
                    <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Fundo Alumni</h1>
                    <p className="text-gray-500 text-base font-light mt-1">
                        Apoie o crescimento e desenvolvimento da Universidade Eduardo Mondlane
                    </p>
                </div>
            </div>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-emerald-50/50 to-gray-50/50 rounded-xl p-8 mb-8 border border-emerald-100/30">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Banknote className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-xl font-light text-gray-700">O que é o Fundo Alumni?</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-3">
                    O <span className="font-medium text-emerald-600">Fundo Alumni – UEM</span> é uma estrutura de recursos da Comunidade Alumni – UEM, 
                    criada para apoiar o crescimento institucional e promover o desenvolvimento da universidade.
                </p>
                <p className="text-gray-600 leading-relaxed">
                    A gestão do Fundo Alumni – UEM integra uma representação da Universidade Eduardo Mondlane, 
                    através da Direcção do Registo Académico e de Representantes da Comunidade Alumni – UEM.
                </p>
            </div>

            {/* Objective Section */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
                <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-medium text-gray-700">Objectivo Principal</h2>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                    O principal objectivo do Fundo Alumni – UEM insere-se no contexto de 
                    <span className="font-medium text-emerald-600"> apoio ao crescimento da UEM</span>, 
                    como instituição de ensino superior de referência, suportando a realização das actividades 
                    da Comunidade Alumni-UEM.
                </p>
            </div>

            {/* Achievements Section */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 mb-8">
                <div className="flex justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-white/80" />
                </div>
                <h3 className="text-lg font-light text-white text-center mb-6">
                    Impacto das Contribuições
                </h3>
                <div className="flex flex-row justify-around items-center">
                    {achievements.map((achievement, index) => (
                        <React.Fragment key={index}>
                            <div className="text-center">
                                <div className="text-2xl font-light text-white mb-1">
                                    {achievement.value}
                                </div>
                                <div className="text-xs text-emerald-100">
                                    {achievement.label}
                                </div>
                            </div>
                            {index < achievements.length - 1 && (
                                <div className="w-px h-8 bg-emerald-500/50"></div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* How to Contribute Section */}
            <div className="bg-gray-50/50 rounded-xl p-6 mb-8 border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                    <HandHeart className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-medium text-gray-700">Como Contribuir</h2>
                </div>
                <p className="text-gray-500 text-sm mb-4">
                    A Comunidade Alumni tem apoiado diversas causas, graças às generosas contribuições dos seus Alumni.
                </p>
                <ContributionWays />
            </div>

            {/* Bank Details Section */}
            <div className="mb-8">
                <BankDetails />
            </div>

            {/* Call to Action Section */}
            <div className="bg-gradient-to-r from-emerald-50/50 to-transparent rounded-xl p-6 text-center border border-emerald-100/30">
                <Heart className="w-6 h-6 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-700 mb-2">
                    Faça Parte Desta Causa
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                    Sua contribuição ajuda a transformar vidas e fortalecer a excelência académica na UEM
                </p>
                <button 
                    onClick={() => window.location.href = "/contactos"}
                    className="bg-emerald-600 text-white px-5 py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
                >
                    Entre em Contacto
                </button>
            </div>

            {/* Footer Note */}
            <div className="mt-8 text-center">
                <p className="text-xs text-gray-400">
                    Todos os recursos são geridos de forma transparente e aplicados em benefício da comunidade académica
                </p>
            </div>
        </div>
    );
};

export default FundoAlumni;